import { useState } from "react"
import { API_ORIGIN } from "@/constants"
import { OnboardingStep } from "@/hooks/use-onboarding"
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { FormField } from "@/components/ui/form-field"
import { Textarea } from "@/components/ui/textarea"
import { OnboardingStepNext, runOnboardingStepNext } from "@/lib/onboarding-step-nav"

interface PreferencesStepProps {
  step: OnboardingStep
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

export function PreferencesStep({ step, onNext, onBack, isCompleting, canGoBack }: PreferencesStepProps) {
  const [preferences, setPreferences] = useState<Record<string, any>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Generic preferences form - can be customized per client
  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`${API_ORIGIN}/api/profiles/onboarding/preferences`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          preferences: preferences,
        }),
      })

      if (response.ok) {
        await runOnboardingStepNext(onNext, setError)
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.error || 'Failed to save preferences')
      }
    } catch (err) {
      console.error('Error saving preferences:', err)
      setError('Network error. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <p className="text-muted-foreground">
          {step.description || "Set your preferences."}
        </p>
        
        {/* Generic preferences form - can be extended per client */}
        <FormField label="Preferences">
          <Textarea
            value={JSON.stringify(preferences, null, 2)}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value)
                setPreferences(parsed)
              } catch {
                // Invalid JSON, ignore
              }
            }}
            disabled={saving || isCompleting}
            rows={6}
            placeholder='{"key": "value"}'
          />
        </FormField>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleSave}
        backDisabled={saving || isCompleting}
        continueDisabled={saving || isCompleting}
        isBusy={saving}
        continueBusyLabel="Saving..."
      />
    </div>
  )
}
